import React, { FC, useEffect, useState, useCallback } from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import Grid from 'components/UI/Grid';
import api from 'Api/ApiMethods';
import { VpdThreshold } from 'models/restTypes';
import { CropType } from 'models/shared';
import VpdThresholdsTable from './VpdThresholdsTable';
import MenuBar from './MenuBar';

const Container = styled(Grid)`
  height: 100%;
  width: 100%;
  justify-content: flex-start;
  padding: 1rem 1rem 1rem 1rem;
`;

const VpdThresholds: FC = observer(() => {
  const [data, setData] = useState<VpdThreshold[]>([]);
  const [cropTypes, setCropTypes] = useState<CropType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedThreshold, setSelectedThreshold] =
    useState<VpdThreshold | null>(null);

  const fetchThresholds = useCallback(async () => {
    setLoading(true);
    try {
      const vpdThresholds = api.getVpdThresholds();
      const cropTypesApi = api.getCropTypes();

      const [thresholdsResponse, cropTypesResponse] = await Promise.all([
        vpdThresholds,
        cropTypesApi,
      ]);

      setCropTypes(cropTypesResponse);
      setData(thresholdsResponse);

    } catch (error) {
      console.error('Error fetching VPD thresholds or crop types', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchThresholds();
  }, [fetchThresholds]);

  const handleThresholdUpdated = useCallback(
    (updatedThreshold: VpdThreshold) => {
      setData((prevData) =>
        prevData.map((threshold) =>
          threshold.cropId === updatedThreshold.cropId
            ? updatedThreshold
            : threshold,
        ),
      );

      setSelectedThreshold(updatedThreshold);
    },
    [],
  );

  return (
    <Container>
      <MenuBar
        selectedThreshold={selectedThreshold}
        onThresholdUpdated={handleThresholdUpdated}
      />
      <VpdThresholdsTable
        thresholds={data}
        isLoading={loading}
        selectedThreshold={selectedThreshold}
        cropTypes={cropTypes}
        onSelectionChange={setSelectedThreshold}
      />
    </Container>
  );
});

export default VpdThresholds;
